import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip,
} from "reactstrap";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import TableContainer from "../../../../components/Common/TableContainer";
import { Link } from "react-router-dom";
import useButtonAccess from "components/Common/useButtonAccess";
import { useLocation, useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import CustomSelect from "components/Common/custom-select";
import CustomDatePicker from "common/CustomDatePicker";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeProjectBillingStatus, fetchProject, getProjectStatusBilling, saveProjectStatusBilling } from "store/actions";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const SelectcustomStyles = {
  control: (base) => ({ ...base, minHeight: 33.1 }),
  indicatorsContainer: (base) => ({ ...base, padding: "0" }),
  dropdownIndicator: (base) => ({ ...base, padding: "2px 8px" }),
  clearIndicator: (base) => ({ ...base, padding: "2px 8px" }),
};

const TimesheetProjectBilling = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  const { canRead, canCreate, canEdit, canDelete, canApprove} = useButtonAccess(accessRight);

  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);

  const {
    project,
    projectStatusBillingList,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    project: state.TimesheetSetupProjectReducer?.project ?? [],
    projectStatusBillingList: state.TimesheetSetupProjectReducer?.projectStatusBillingList ?? [],
    loading: state.TimesheetSetupProjectReducer?.loading,
    error: state.TimesheetSetupProjectReducer?.error,
    success: state.TimesheetSetupProjectReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    const create_by = authUserDetails[0].user_id;
    dispatch(fetchProject(unitId, create_by));
    const formData = new FormData();
    formData.append("unit_id", unitId);
    dispatch(getProjectStatusBilling(formData));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      const formData = new FormData();
      formData.append("unit_id", authDetails?.unit_id);
      dispatch(getProjectStatusBilling(formData));
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const projectOptions = Array.isArray(project)
    ? project.map((p) => ({
      value: p.project_id,           // store ID
      label: `${p.project_code} - ${p.project_name}`,
    }))
    : [];

  const currencyOptions = [
    { value: 1, label: "INR" },
    { value: 2, label: "SGD" },
  ];

  const formik = useFormik({
    initialValues: {
      project: "",
      projectValue: "",
      amountReceived: "",
      currency: "",
      remarks: "",
      nextDue: "",
    },
    validationSchema: Yup.object({
      project: Yup.string().nullable().required("Project is required"),
      currency: Yup.number().nullable().required("Currency is required"),
      projectValue: Yup.number().typeError("Project value must be a number").positive("Project value must be greater than 0").required("Project value is required"),
      amountReceived: Yup.number().typeError("Amount received must be a number").min(0, "Amount received cannot be negative").required("Amount received is required"),
      nextDue: Yup.date().nullable().required("Next due date is required").typeError("Invalid date"),
    }),
    onSubmit: (values, { resetForm }) => {
      console.log("Form Values:", values);
      const formData = new FormData();

      formData.append("pro_bill_id", isEdit ? editId : 0);
      formData.append("project_id", String(values.project));
      formData.append("currency_type", String(values.currency));
      formData.append("project_value", String(values.projectValue));
      formData.append("amt_received", String(values.amountReceived));
      formData.append("remarks", values.remarks || "");
      formData.append("next_due_dt", values.nextDue || "");
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);

      dispatch(saveProjectStatusBilling(formData));
    },
  });

  const handleEdit = (row) => {
    formik.setValues({
      project: row.project_id ?? null,
      projectValue: row.project_value ? String(Math.trunc(row.project_value)) : "",
      amountReceived: row.amount_received ? String(Math.trunc(row.amount_received)) : "",
      currency: row.currency_type ?? null,
      remarks: row.remarks ?? "",
      nextDue: row.next_due_dt ? dayjs(row.next_due_dt, "DD/MM/YYYY").format("YYYY-MM-DD") : null,
    });

    setIsEdit(true);
    setEditId(row.pro_bill_id);
  };

  const handleToggleStatus = (id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${isActive ? 'deactivate' : 'activate'} this project billing.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeProjectBillingStatus(formData));
      }
    });
  };

  const columns = [
    {
      Header: "Action",
      Cell: ({ row }) => {
        const { pro_bill_id, b_active } = row.original;

        return (
          <div className="d-flex gap-3">

            {/* DELETE / STATUS */}
            {canDelete && (
              <Link
                to="#"
                className="me-2"
                style={{ color: b_active ? "green" : "red" }}
                onClick={() => handleToggleStatus(pro_bill_id, b_active)}
              >
                <i
                  className={`mdi ${b_active ? "mdi-check-circle" : "mdi-close-circle"
                    } font-size-18`}
                  style={{ color: b_active ? "green" : "red" }}
                  id={`status-tooltip-${pro_bill_id}`}
                />
                <UncontrolledTooltip
                  placement="top"
                  target={`status-tooltip-${pro_bill_id}`}
                >
                  {b_active ? "Active" : "Inactive"}
                </UncontrolledTooltip>
              </Link>
            )}

            {/* EDIT */}
            {canEdit && (
              <Link
                to="#"
                className="text-success"
                onClick={() => handleEdit(row.original)}
              >
                <i
                  className="bx bxs-edit font-size-18"
                  id={`edit-tooltip-${row.id}`}
                />
                <UncontrolledTooltip
                  placement="top"
                  target={`edit-tooltip-${row.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </Link>
            )}

          </div>
        );
      },
    },
    {
      Header: "S.No",
      className: "serial-no",
      applyHeaderClass: true,
      Cell: ({ row }) => projectStatusBillingList.length - row.index,
    },
    { Header: "Project", accessor: "project_name" },
    { Header: "Project Value", accessor: "project_value" },
    { Header: "Amount Received", accessor: "amount_received" },
    { Header: "Currency", accessor: "currency" },
    { Header: "Remarks", accessor: "remarks" },
    { Header: "NextDue", accessor: "next_due_dt" },
    { Header: "Created By", accessor: "created_by" },
    { Header: "Created On", accessor: "created_on" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Timesheet" breadcrumbItem="Project Status" />
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row className="mb-2">
                <Col md={3}>
                  <FormGroup>
                    <Label>Project <span className="text-danger">*</span></Label>
                    <CustomSelect
                      options={projectOptions}
                      onChange={(selected) => {
                        formik.setFieldValue("project", selected ? selected.value : null);
                      }}
                      onBlur={() => formik.setFieldTouched("project", true)}
                      value={
                        projectOptions.find(
                          (opt) => opt.value === formik.values.project
                        ) || null
                      }
                      styles={SelectcustomStyles}
                    />
                    {formik.errors.project && formik.touched.project && (
                      <div className="text-danger">{formik.errors.project}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Currency <span className="text-danger">*</span></Label>
                    <CustomSelect
                      options={currencyOptions}
                      onChange={(selected) => {
                        formik.setFieldValue("currency", selected ? selected.value : null);
                      }}
                      onBlur={() => formik.setFieldTouched("currency", true)}
                      value={
                        currencyOptions.find(
                          (opt) => opt.value === formik.values.currency
                        ) || null
                      }
                      styles={SelectcustomStyles}
                    />
                    {formik.errors.currency && formik.touched.currency && (
                      <div className="text-danger">{formik.errors.currency}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Project Value <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      name="projectValue"
                      value={formik.values.projectValue}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={formik.errors.projectValue && formik.touched.projectValue ? "is-invalid" : ""}
                    />
                    {formik.errors.projectValue && formik.touched.projectValue && (
                      <div className="text-danger">{formik.errors.projectValue}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Amount Received <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      name="amountReceived"
                      value={formik.values.amountReceived}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={formik.errors.amountReceived && formik.touched.amountReceived ? "is-invalid" : ""}
                    />
                    {formik.errors.amountReceived && formik.touched.amountReceived && (
                      <div className="text-danger">{formik.errors.amountReceived}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Next Due <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                      name="nextDue"
                      value={formik.values.nextDue}
                      onChange={(date) => {
                        formik.setFieldValue("nextDue", date);
                      }}
                      onBlur={formik.handleBlur}
                      className={`form-control ${formik.touched.nextDue && formik.errors.nextDue ? "is-invalid" : ""}`}
                      disablePast={true}
                    />
                    {formik.errors.nextDue && formik.touched.nextDue && (
                      <div className="text-danger">{formik.errors.nextDue}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Remarks</Label>
                    <Input
                      type="text"
                      name="remarks"
                      value={formik.values.remarks}
                      onChange={formik.handleChange}
                    />
                  </FormGroup>
                </Col>
                <Col className="d-flex mob-center align-items-center justify-content-end mt-2">
                  {canCreate && (
                    <Button type="submit" color="primary" className="btn button-save me-2">
                      <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                    </Button>
                  )}
                  <Button type="button" color="secondary" onClick={() => {
                    formik.resetForm();
                    setIsEdit(false);
                    setEditId(null);
                  }} className="btn button-cancel" >
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </Col>
              </Row>

            </Form>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={projectStatusBillingList}
              isGlobalFilter={true}
              customPageSize={10}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default TimesheetProjectBilling;
